import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router";
import { toast } from "react-toastify";
import { ReactSVG } from "react-svg";

import { formatApiErrors } from "../utils/formatApiErrors";
import { google_logo } from "../assets/images/images";
import {
  GoogleAuthProvider,
  auth,
  signInWithPopup,
} from "../config/firebaseConfig";
import api from "../api/api";
import ChangePassword from "../components/ChangePassword/ChangePassword";
import { initGoogleSignIn, requestGoogleSignIn } from "../config/googleIdentity";

// Signup starts with activeTab as "" (useState(location.state?.role || "")),
// not null. Every role guard here used to test only null/undefined, so the
// empty string slipped through: the user clicked Sign Up with Google without
// choosing a role, the guard passed, role went up as null, and the backend
// answered "User with this email does not exist." Treat anything empty as
// "no role chosen".
const hasRole = (activeTab) => Boolean(activeTab);

// Ways the user can walk away from a sign-in popup on purpose. The first three
// are what Google Identity Services reports; the auth/* codes are Firebase's
// names for the same thing on the fallback path. None is an error worth
// interrupting someone with.
const USER_DISMISSED = new Set([
  "popup_closed",
  "access_denied",
  "user_cancel",
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/user-cancelled",
]);

const GoogleInUp = (props) => {

  const [userId, setuserId] = useState("");
  const [changePassword, setchangePassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get("redirect");
  if (redirectPath) {
    localStorage.setItem("redirectAfterSignup", redirectPath);
  }
  const redirectLink = localStorage.getItem("redirectAfterSignup");

  const redirectCourse = searchParams.get("redirectToCourse");
  if (redirectCourse) {
    localStorage.setItem("redirectToCourse", redirectCourse);
  }

  const redirectToCoursePath = localStorage.getItem("redirectToCourse");

  const loginWithGoogle = async (e) => {
    e.preventDefault();

    // Signup needs a role; signing in does not — the backend resolves it from
    // the email. Same rule as the GIS path above, kept in step so the fallback
    // behaves identically to the primary route.
    if (props.forContent === "Sign Up with Google" && !hasRole(props.activeTab)) {

      props.setRoleValid(true);
      return;
    }
    const provider = new GoogleAuthProvider();
    // Same reason as the GIS path: without this, Firebase reuses whichever
    // Google account the browser profile is signed into, so a user whose
    // profile account is not registered here sees the popup flash shut and
    // "user does not exist", with no opportunity to choose the address that
    // would have worked. This has been the behaviour in production all along.
    provider.setCustomParameters({ prompt: "select_account" });

    try {
      const result = await signInWithPopup(auth, provider);

      const user = result._tokenResponse;
      // const userEmailid = result?._tokenResponse?.email;

      if (user) {

        const { emailVerified } = user;

        handleUserLogin(emailVerified, result.user.accessToken);
      } else {
        // Previously this branch did nothing at all: no error, no toast, no
        // log. The popup closed and the page sat there, which is
        // indistinguishable from the app being broken and produces no signal
        // to debug from.
        console.error("Google login returned no token response", result);
        toast.error("Google sign-in did not complete. Please try again.", {
          position: "top-center",
        });
        props.setLoading?.(false);
      }
    } catch (error) {
      // The code is shown to the user on purpose. Firebase reports exactly why
      // sign-in failed — auth/popup-blocked, auth/unauthorized-domain,
      // auth/admin-restricted-operation and so on — and this used to replace
      // all of them with one sentence that identified none. When a whole school
      // could not log in, the cause existed only in the console of a browser
      // three thousand miles away, and diagnosing it took a day.
      //
      // Showing it means the next person who reports a failure reports the
      // cause with it.
      const code = error?.code || error?.message || "unknown";
      console.error("Error with Google login:", code, error);
      // Closing this popup is not a failure either. Firebase has its own names
      // for it, so they are excluded here the same way the GIS path excludes
      // its own — otherwise falling back to Firebase would reintroduce exactly
      // the toast the user asked to be rid of.
      if (!USER_DISMISSED.has(code)) {
        toast.error(`Login failed (${code}). Please try again.`, {
          position: "top-center",
        });
      }
      props.setLoading?.(false);
    }
  };

  const onChangePasswordClose = () => {
    setchangePassword(!changePassword);
  };

  // The backend rejects a Google sign-in for a handful of reasons, and two of
  // them are fixed by choosing a different Google account. The raw message does
  // not say so, which left users staring at "user does not exist" with no idea
  // that the account they wanted was one click away.
  const withAccountHint = (message) => {
    const text = message || "";
    const switchable =
      /does not exist|already exists|organization approved/i.test(text);
    return switchable
      ? `${text} Click ${props.forContent} again to choose a different account.`
      : text;
  };

  const handleUserLogin = async (emailVerified, accessToken) => {
    const failedErrorMessage =
      "Oops, something went wrong. Please try again later.";
    try {
      if (emailVerified) {
        let type = null;
        if (props.activeTab === "Student") {
          type = 1;
        } else if (props.activeTab === "Teacher") {
          type = 2;
        } else if (props.activeTab === "Parent") {
          type = 3;
        }


        try {

          const response = await api.post(`auth/google-login/v2/`, props.forContent === "Sign Up with Google" ? {
            role: type,
            key: accessToken,
          } : {
            key: accessToken
          });
          if (response?.status === 400) {
            toast.dismiss();
            const error = formatApiErrors(response?.data);
            toast.error(
              withAccountHint(error || response?.data?.message) || failedErrorMessage,
              {
                position: "top-center",
              }
            );
            props.setLoading(false);
            return;
          }
          if (response?.data?.first_login &&
            response?.data?.token === undefined) {
            toast.dismiss();
            setuserId(response?.data?.user_id);
            setchangePassword(true);
          }
          if (response?.data?.token === undefined) {
            toast.dismiss();
            const error = formatApiErrors(response?.data);
            toast.error(
              withAccountHint(error || response?.data?.message) || failedErrorMessage,
              {
                position: "top-center",
              }
            );
            props.setLoading(false);
            return;
          }
         localStorage.setItem("is_profile", String(Boolean(response?.data?.is_profile)));
          let role = null
          if (response?.data?.data?.role === 1) {
            role = 'Student'
          } else if (response?.data?.data?.role === 2) {
            role = 'Teacher'
          } else if (response?.data?.data?.role === 3) {
            role = 'Parent'
          } else if (response?.data?.data?.role === 4) {
            role = 'School Admin'
          } else if ((response?.data?.data?.role === 5)) {
            role = 'Super Admin'
          }


          props.login(response?.data?.token, role, response?.data?.data?.email || "");
          toast.dismiss();
          toast.success("Login successful.", {
            position: "top-center",
          });
          if (role === "Student") {
            navigate("/dashboard/home", { state: { role: role } });
          } else if (role === "Teacher") {
            navigate("/dashboard/home", { state: { role: role } });
          } else if (role === "Parent") {
            role = "parent";
          }
          props.setLoading(false);
        } catch (error) {

          toast.dismiss();
          const errorMessage = formatApiErrors(error?.response?.data);
          toast.error(errorMessage || failedErrorMessage, {
            position: "top-center",
          });
          props.setLoading(false);
        }
      } else {
        // The third silent branch. An unverified Google address fell through
        // here and produced nothing on screen, so the user was told neither
        // that it failed nor why.
        toast.error(
          "Your Google account's email address is not verified. Verify it with Google and try again.",
          { position: "top-center" }
        );
        props.setLoading?.(false);
      }
    } catch (error) {

      console.error("Error fetching user info", error);
      toast.error("Could not complete sign-in. Please try again.", {
        position: "top-center",
      });
      props.setLoading?.(false);
    }
  };

  // Google Identity Services renders its own button; GIS has no supported way
  // to start this flow from ours. Everything below the credential callback is
  // unchanged — the ID token goes to the same endpoint, in the same `key`
  // field, as the Firebase token did.
  const isSignUp = props.forContent === "Sign Up with Google";

  // GIS keeps the callback it was initialised with, so reading props through a
  // ref avoids re-rendering the button every time the parent re-renders — and
  // avoids the callback closing over a stale activeTab, which decides the role
  // a new account is created with.
  const propsRef = useRef(props);
  useEffect(() => {
    propsRef.current = props;
  });

  // handleUserLogin is rebuilt every render and reads props.activeTab and
  // props.forContent straight out of its own closure. The credential callback
  // below cannot list it as a dependency — that would re-initialise GIS on
  // every parent render — so it is reached through a ref that is repointed at
  // the current one after each render.
  //
  // Without this, the callback kept the version built on first render, when
  // activeTab was still "" because the user had not yet chosen a role. The
  // guard in the callback reads propsRef, so it saw the chosen role and let
  // the signup through; the stale closure it then called still sent role as
  // null, and the backend answered "User with this email does not exist." on a
  // signup that plainly had a role selected.
  const handleUserLoginRef = useRef(null);
  useEffect(() => {
    handleUserLoginRef.current = handleUserLogin;
  });

  const handleGoogleCredential = useCallback(async (credential) => {
    const current = propsRef.current;

    if (!credential) {
      // No credential here means the popup went away without one, and closing
      // it is by far the most common reason. Logged, not announced.
      console.warn("Google sign-in produced no credential");
      current.setLoading?.(false);
      return;
    }

    // Role is only needed to CREATE an account. Signing in sends just the
    // credential and the backend resolves the role from the account the email
    // belongs to, so asking which role to sign in as is both unnecessary and
    // something the user may answer wrongly about their own account.
    const signingUp = current.forContent === "Sign Up with Google";
    if (signingUp && !hasRole(current.activeTab)) {
      current.setRoleValid?.(true);
      current.setLoading?.(false);
      return;
    }

    current.setLoading?.(true);
    // Google asserts email_verified inside the ID token and the backend
    // rejects it when false, so there is nothing left for the client to check.
    await handleUserLoginRef.current?.(true, credential);
  }, []);

  // True once Google Identity Services is ready. Until then — and permanently
  // if the GIS script is blocked, which school networks do — the button falls
  // back to the original Firebase popup, so sign-in never simply stops working.
  const [googleReady, setGoogleReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    initGoogleSignIn((credential, error) => {
      if (cancelled) return;
      if (!credential) {
        // Nothing is shown to the user here, whatever the reason.
        //
        // Closing the popup is a decision, not a failure, and Google reports
        // it through the same channel as genuine errors — popup_closed,
        // access_denied and a blocked popup all arrive looking alike. Rather
        // than guess which strings mean "the user changed their mind", this
        // stays quiet for all of them: an unwanted toast on every dismissal
        // is worse than a missing one on a rare failure.
        //
        // The reason is still written to the console, so a blocked popup is
        // diagnosable from a screen share even though it is not announced.
        console.warn("Google sign-in did not return a credential:", error);
        propsRef.current.setLoading?.(false);
        return;
      }
      handleGoogleCredential(credential);
    }).then((ready) => {
      if (!cancelled) setGoogleReady(ready);
    });

    return () => {
      cancelled = true;
    };
  }, [handleGoogleCredential]);

  // One handler for the one button. Through Google when available, through the
  // original Firebase popup when not.
  const handleGoogleClick = (e) => {
    e.preventDefault();

    if (isSignUp && !hasRole(props.activeTab)) {
      props.setRoleValid?.(true);
      return;
    }

    if (googleReady && requestGoogleSignIn()) {
      props.setLoading?.(true);
      return;
    }

    loginWithGoogle(e);
  };

  return (
    <>
      {/* Your approved button. Google Identity Services will not mint an ID
          token from a custom element, so this uses the token client instead
          and the server verifies the access token with Google. One extra
          outbound call per sign-in — the same cost the Firebase path already
          paid to read the user's email back.

          One button, two routes: Google when GIS is available, the original
          Firebase popup when it is not. A school network blocking
          accounts.google.com/gsi/client is a real failure here, not a
          theoretical one, and sign-in has to survive it. */}
      <button
        type="button"
        disabled={props.loading}
        className="login-btns google-signin-btn"
        onClick={handleGoogleClick}
        aria-label={props.forContent}
      >
        <ReactSVG src={google_logo} />
        <p>{props.forContent}</p>
      </button>
      {changePassword && (
        <ChangePassword
          changePassword={changePassword}
          onChangePasswordClose={onChangePasswordClose}
          userId={userId}
        />
      )}
    </>
  );
};

export default GoogleInUp;